import { Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { Toaster } from "sonner";

import { TOASTER_CONFIG } from "@/utils/toaster-config";
import logo from "@/assets/logo/logo.png";
import {
  OnboardingSidebarProvider,
  useOnboardingSidebar,
} from "@/context/onboarding-sidebar-context";
import { cn } from "@/lib/utils";

interface OnboardingLayoutProps {
  sidebarContent?: React.ReactNode;
  sidebarKey?: string;
}

const OnboardingLayoutContent = ({ sidebarClassName }: { sidebarClassName?: string }) => {
  const { sidebarContent, sidebarKey, headerAction, scrollContainerRef } = useOnboardingSidebar();
  const hasSidebar = Boolean(sidebarContent);

  return (
    <>
      <div className="h-screen w-full overflow-hidden">
        <div
          className={`grid h-full ${hasSidebar ? "grid-cols-1 lg:grid-cols-[0.56fr_0.44fr] max-w-1480:grid-cols-[1fr]" : "grid-cols-1"}`}
        >
          <div className="flex h-full flex-col overflow-hidden">
            <header className="relative z-[2] flex flex-shrink-0 items-center justify-between p-8 phone:px-4">
              <img src={logo} alt="PromptLayer" className="h-[37px]" />
              {headerAction}
            </header>

            <main
              ref={scrollContainerRef}
              className="relative z-[1] flex flex-1 justify-center overflow-y-auto px-4 pb-8 sm:px-16 lg:px-24 xl:px-32 phone:px-4 phone:pb-8"
            >
              <div className="my-auto flex w-fit">
                <Outlet />
              </div>
            </main>
          </div>

          {hasSidebar && (
            <div
              className={cn(
                "hidden h-full w-full overflow-hidden rounded-bl-[82px] rounded-tl-[82px] bg-gradient-to-br from-[#1439A0] to-[#B1C6FF] lg:flex max-w-1480:hidden",
                sidebarClassName,
              )}
            >
              <motion.div
                key={sidebarKey || "promo-section"}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex h-full w-full flex-col items-center justify-center"
              >
                {sidebarContent}
              </motion.div>
            </div>
          )}
        </div>
      </div>
      <Toaster {...TOASTER_CONFIG} />
    </>
  );
};

export const OnboardingLayout = ({ sidebarContent, sidebarKey }: OnboardingLayoutProps = {}) => (
  <OnboardingSidebarProvider sidebarContent={sidebarContent} sidebarKey={sidebarKey || "default"}>
    <OnboardingLayoutContent sidebarClassName="bg-transparent" />
  </OnboardingSidebarProvider>
);
